import React, { useEffect } from "react";
import { Stack, Box, Container, LinearProgress, Divider } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CircleIcon from '@mui/icons-material/Circle';
import { useNavigate } from "react-router-dom";
import LiveBalanceWidget from "../liveloans/account/LiveBalanceWidget";
import ActionLinkCard from "../account/ActionLinkCard";
import ArrearsCard from "../liveloans/account/ArrearsCard";
import Banner from "../nav/Banner";
import SettlementOffer from "../widgets/account/SettlementOffer";
import SettlementWidget from "../widgets/account/Settlement";
import ScriptLoader from "../account/ScriptLoader";
import { useClient } from "../../context/ClientProvider";
import { useNav } from "../../context/NavProvider";
import { useAccount } from "../../context/AccountProvider";
import { useSfs } from "../../context/SfsProvider";
import { useArrangement } from "../../context/ArrangementProvider";
import { useNotifications } from "../../context/NotificationsProvider";
import SettlementProvider from "../../context/SettlementProvider";
import { useLiveLoans } from "../../context/LiveLoansProvider";
import { formatCurrency } from '../../utils/formatCurrency';

import LiveLoansArrangementWidget from "../liveloans/arrangement/LiveLoansArrangementWidget";
import DefaultCard from "../liveloans/account/DefaultCard";
import AboutMyLoanCard from "../liveloans/account/AboutMyLoanCard";
import dayjs from "dayjs";
import EarlySettlementCard from "../liveloans/account/EarlySettlementCard";



function LiveLoansAccountPage() {
  const navigate = useNavigate();
  const { settlementConfig } = useClient();
  const { setShowFooter } = useNav();
  setShowFooter(false);

  const { useGetAccount, useGetLiveAccount } = useAccount();
  const { useGetSfs, loadSfsData, sfsLoaded, disposableIncome } = useSfs();
  const {
    useGetArrangement,
    useGetArrangementHistory,
    loadPlanData,
    planLoaded,
    lastSavedPlan,
  } = useArrangement();
  const { liveAccount, isLiveAccountLoading } = useGetLiveAccount();
  const { account, isAccountLoading } = useGetAccount();
  const { sfs, isSfsLoading } = useGetSfs();
  const { arrangement, isArrangementLoading } = useGetArrangement();
  const { arrangementHistory, isArrangementHistoryLoading } = useGetArrangementHistory();

  const { useGetAccessibility } = useNotifications();
  const { accessibility, isAccessibilityLoading } = useGetAccessibility();

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  const { useGetPaymentDateChanges, useGetLiveSettlement } = useLiveLoans();
  const { paymentDateChanges, isPaymentDateChangesLoading } = useGetPaymentDateChanges();
  const { liveSettlement, isLiveSettlementLoading } = useGetLiveSettlement();

  Date.prototype.loseDays = function (days) {
    var date = new Date(this.valueOf());
    date.setDate(date.getDate() - days);
    return date;
  }
  const now = new Date();
  console.log(`displayFrequency: ${clientConfig?.layout?.notifications?.cards?.one?.displayFrequency}`);
  const thresholdDateForRenderingNotificationPage = now.loseDays(clientConfig?.layout?.notifications?.cards?.one?.displayFrequency).toISOString()
  console.log(`thresholdDate: ${thresholdDateForRenderingNotificationPage}`);
  console.log(`Accessibility modified date: ${accessibility?.modified}`)

  useEffect(() => {
    if (clientConfig?.layout?.notifications?.showNotifications && clientConfig?.layout?.accessibility?.showAccessibilityModule && !isAccessibilityLoading && ((accessibility?.modified < thresholdDateForRenderingNotificationPage) || (accessibility?.modified === accessibility?.created)) && !(localStorage.getItem("remindMeLaterClicked") || localStorage.getItem("viewMyAccountClicked") || localStorage.getItem("startAccessibilityClicked") || localStorage.getItem("accessibilitySubmitted"))) {
      navigate("/notifications")
    }
  }, [isAccessibilityLoading])

  useEffect(() => {
    if (
      !isAccountLoading &&
      !isArrangementLoading &&
      !isArrangementHistoryLoading &&
      !planLoaded &&
      sfsLoaded
    ) {
      loadPlanData(arrangement, arrangementHistory);
    }

    if (!sfsLoaded && !isSfsLoading) {
      loadSfsData(sfs);
    }
  }, [
    planLoaded,
    isSfsLoading,
    sfsLoaded,
    isArrangementLoading,
    isArrangementHistoryLoading,
    loadPlanData,
    loadSfsData,
    arrangement,
    arrangementHistory,
    sfs
  ]);

  if (
    isAccountLoading ||
    isSfsLoading ||
    isArrangementLoading ||
    isArrangementHistoryLoading ||
    isAccessibilityLoading ||
    isLiveAccountLoading ||
    isPaymentDateChangesLoading ||
    isLiveSettlementLoading
  ) {
    return <LinearProgress />;
  }

  return (
    <>
      <ScriptLoader />
      {account && planLoaded && sfsLoaded && !isAccessibilityLoading && !isLiveAccountLoading && !isPaymentDateChangesLoading && !isLiveSettlementLoading && (
        <>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Banner />
          </Box>
          <Container sx={{ mt: 4, mb: 7, paddingBottom: "350px" }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              divider={<Divider orientation='vertical' flexItem />}
              spacing={{ xs: 0.05, md: 4 }}
            >
              {/* Column 1 */}
              <Grid container direction={"column"} spacing={2} xs={12} md={6}>
                <LiveBalanceWidget balance={account.currentBalance} />
                <AboutMyLoanCard accountNumber={account?.accountReference} loanStartDate={dayjs(liveAccount?.advanceDate)?.format("DD/MM/YYYY")} loanEndDate={dayjs(liveAccount?.contractEndDate)?.format("DD/MM/YYYY")} agreedLoanAmount={`£${liveAccount?.originalBalance.toFixed(2)}`} currentLoanBalance={`£${account?.currentBalance.toFixed(2)}`}></AboutMyLoanCard>
                {liveSettlement !== null &&
                  <EarlySettlementCard requestedDate={dayjs(liveSettlement?.requestedDate)?.format("DD/MM/YYYY")} expirationDate={dayjs(liveSettlement?.expirationDate)?.format("DD/MM/YYYY")} settlementAmount={`£${liveSettlement?.settlementAmount.toFixed(2)}`} ></EarlySettlementCard>
                }
                {clientConfig?.layout?.arrearsCard?.enabled && account.arrearsAmount !== null && account.arrearsAmount > 0 && !account.defaultStatus &&
                  <Grid>
                    <ArrearsCard balance={formatCurrency(account.arrearsAmount)} paragraphOne={clientConfig?.layout?.arrearsCard?.paragraphs[0]} paragraphTwo={clientConfig?.layout?.arrearsCard?.paragraphs[1]} paragraphThree={clientConfig?.layout?.arrearsCard?.paragraphs[2]} paragraphFour={clientConfig?.layout?.arrearsCard?.paragraphs[3]} paragraphFive={clientConfig?.layout?.arrearsCard?.paragraphs[4]} tel={clientConfig?.layout?.arrearsCard?.tel} telFromAbroad={clientConfig?.layout?.arrearsCard?.telFromAbroad} ></ArrearsCard>
                  </Grid>}
                {account.defaultStatus &&
                  <Grid><DefaultCard tel={clientConfig?.layout?.arrearsCard?.tel}></DefaultCard></Grid>}
                <Grid>
                  <ActionLinkCard
                    id='make-a-payment-container'
                    backgroundColor='#FFF'
                    textColor='#000'
                    title='Here to make a one off payment?'
                    description='If you would like to make a one off payment towards your balance click the button below;'
                    buttonId='make-payment-btn'
                    buttonColor='#FFF'
                    buttonTextColor='secondary.main'
                    borderColor='secondary.main'
                    buttonTitle='Make a payment'
                    hoverColor='secondary.main'
                    hoverBorderColor={'secondary.main'}
                    hoverTextColor='secondary.contrastText'
                    target='/payment'
                    icon={<CreditCardIcon sx={{ color: "#000" }} />}
                  />
                </Grid>
                {!sfs ? (
                  <Grid>
                    <ActionLinkCard
                      id='income-expenditure-container'
                      backgroundColor='#FFF'
                      textColor='#000'
                      title={
                        <>
                          Complete your financial statement{" "}
                          <CircleIcon sx={{ color: "secondary.main", fontSize: "18px", verticalAlign: "top" }} />
                        </>
                      }
                      description='You currently don’t have an up-to-date Financial Statement on record. Please click the button below to get started.'
                      buttonId='complete-here-btn'
                      buttonTextColor={clientConfig?.layout?.tertiaryGenericButtonColour || 'secondary.main'}
                      borderColor={clientConfig?.layout?.tertiaryGenericButtonBackgroundColour || 'secondary.main'}
                      buttonTitle='Start my financial statement'
                      hoverColor='secondary.dark'
                      hoverTextColor='#FFF'
                      target='/sfs'
                      icon={<AccountBalanceWalletIcon sx={{ color: "#000" }} />}
                    />
                  </Grid>
                ) : (
                  <Grid>
                    <ActionLinkCard
                      id='income-expenditure-container'
                      backgroundColor='#FFF'
                      textColor='#000'
                      title={"Your financial statement is up to date"}
                      disposableIncome={disposableIncome}
                      buttonId='complete-here-btn'
                      buttonColor='#FFF'
                      buttonTextColor='primary.dark'
                      buttonTitle='Change my financial statement'
                      borderColor='primary.dark'
                      hoverColor='primary.dark'
                      hoverBorderColor={"primary.dark"}
                      hoverTextColor='secondary.contrastText'
                      target='/sfs'
                      icon={<AccountBalanceWalletIcon sx={{ color: "#000" }} />}
                    />
                  </Grid>
                )}
              </Grid>
              {/* Column 2 */}
              <Grid container direction={"column"} spacing={2} xs={12} md={6}>
                {/* Show settlement offer if applicable, and current arrangement is not a settlement or pending payment */}
                {(settlementConfig?.required && !lastSavedPlan?.isSettlement && !lastSavedPlan?.inPaymentWindow) &&
                  <SettlementProvider>
                    <SettlementOffer balance={account?.currentBalance} />
                  </SettlementProvider>
                }
                {lastSavedPlan?.isSettlement ? (
                  <SettlementWidget />
                ) : (
                  <LiveLoansArrangementWidget paymentDateChanges={paymentDateChanges} defaultStatus={account.defaultStatus} />
                )}
              </Grid>
            </Stack>
          </Container>
          {setShowFooter(true)}
        </>
      )}
    </>
  );
}

export default LiveLoansAccountPage;
